import React, { useState, Fragment, useMemo, useEffect } from 'react'
import { Col, Row } from 'react-bootstrap'
import { withStyles, Typography, Card, Box, Tooltip } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import Form, {
  SelectDashboard,
  Input,
  Checkbox,
  Dropzone,
  SubmitButton,
  Button,
} from 'Components/Form'
import BudgetInput from '../ClientEdit/BudgetInput'
import { required } from 'Utils/Validators/formValidation'
import TaxExemptionSection from '../TaxExemption'

import {
  EDDM_SIGNUP,
  INDUSTRY_GROUPS_OPTIONS_TYPES_V2,
  INDUSTRY_GROUPS_V2,
  INDUSTRY_OPTIONS_TYPES_V2,
  INDUSTRY_OPTIONS_V2,
} from '../../../shared/constants'
import { apiXero } from '../../../Module/API/Xero'

import { CLIENT_FORM_MODES } from '../const'
import XeroClient from '../XeroClient'

import { styles } from './styles'
import { CONFIG } from '../../../Config/config'
import { useWrappedPosthog } from '../../../Components/PostHogProvider/hooks'

const startedUser = {
  id: 1,
  firstName: { required: false, value: '', error: false },
  lastName: { required: false, value: '', error: false },
  email: { required: false, value: '', error: false },
  includeInEmails: { value: true, error: false },
}

const ClientForm = ({
  classes,
  defaultValues = {},
  mode,
  handleCancel,
  handleSubmit,
  buttonsDisabled,
  tags,
  countries,
  country,
  client,
  tenant,
  xeroInfo,
  setUsersHashMap,
  usersHashMap = {},
  xeroContact,
  setName = () => {},
  onBlurInputName = () => {},
  handleDeleteTaxExemptionByState = () => {},
  isTaxExemptionEnabled,
  setIsTaxExemptionEnabled = () => {},
  uploadedDocumentsForUnitedStatesStates = [],
  handleAddStateOpenModal = () => {},
}) => {
  const posthog = useWrappedPosthog()
  const isAimFeatureEnabled = posthog.useFeatureFlagEnabled(
    'is_aim_feature_enabled',
  )
  const [file, setFile] = useState(null)
  const [countryId, setCountryId] = useState(country ? country.id : null)
  const [industry, setIndustry] = useState(null)
  const [industryGroup, setIndustryGroup] = useState(null)
  const isEddmSignup = client && client.createdBy === EDDM_SIGNUP

  const isIndustryGroupValid = Object.values(
    INDUSTRY_GROUPS_OPTIONS_TYPES_V2,
  ).includes(defaultValues.industryGroup)
  const isIndustryValid = Object.values(INDUSTRY_OPTIONS_TYPES_V2).includes(
    defaultValues.industry,
  )

  useEffect(() => {
    if (isIndustryGroupValid && isIndustryValid) {
      setIndustryGroup(defaultValues.industryGroup)
      setIndustry(defaultValues.industry)
    }
  }, [isIndustryGroupValid, isIndustryValid])

  const industryGroupOptions = Object.entries(
    INDUSTRY_GROUPS_OPTIONS_TYPES_V2,
  ).map(([key, value]) => ({
    label: key,
    value: value,
  }))
  const industryOptions = useMemo(() => {
    if (!industryGroup) return []
    const groupOptions = INDUSTRY_GROUPS_V2[industryGroup]
    return groupOptions.map(group => ({
      label: INDUSTRY_OPTIONS_V2[group],
      value: group,
    }))
  }, [industryGroup])

  const salesDefaultAccountCode =
    tenant && tenant.availableAccount
      ? `${tenant.availableAccount.code} ${tenant.availableAccount.name}`
      : ''

  const currencies =
    (tenant &&
      tenant.availableCurrencies.map(item => ({
        label: item,
        value: item,
      }))) ||
    []

  let initValues = {
    name: '',
    code: '',
    status: false,
    tags: [],
    logo: '',
    country: '',
    industry: '',
    industryGroup: '',
    isSkipCheckoutEnabled: false,
    sftpEnabled: false,
    addressVerificationEnabled: true,
    addressVerificationDailyBudget: 50,
    addressVerificationMonthlyBudget: 200,
  }
  let xeroInitValues = {
    firstName: '',
    lastName: '',
    emailAddress: '',
    defaultCurrency: currencies[0] ? currencies[0].label : '',
    address1: '',
    address2: '',
    address3: '',
    address4: '',
    city: '',
    region: '',
    postalCode: '',
    salesDefaultLineAmountType: 'EXCLUSIVE',
    trackingCategoryName: defaultValues ? defaultValues.name : '',
    salesDefaultAccountCode,
    attentionTo: '',
  }
  if (mode === CLIENT_FORM_MODES.EDIT) {
    initValues = {
      name: defaultValues.name,
      code: defaultValues.code,
      status: defaultValues.status === 'DELETED',
      tags: defaultValues.tags,
      logo: defaultValues.logo,
      industry: defaultValues.industry,
      industryGroup: defaultValues.industryGroup,
      isSkipCheckoutEnabled: defaultValues.isSkipCheckoutEnabled,
      sftpEnabled: defaultValues.sftpEnabled || false,
      addressVerificationEnabled:
        defaultValues.addressVerificationEnabled !== undefined
          ? defaultValues.addressVerificationEnabled
          : true,
      addressVerificationDailyBudget:
        defaultValues.addressVerificationDailyBudget !== undefined
          ? defaultValues.addressVerificationDailyBudget / 100
          : 50,
      addressVerificationMonthlyBudget:
        defaultValues.addressVerificationMonthlyBudget !== undefined
          ? defaultValues.addressVerificationMonthlyBudget / 100
          : 200,
    }

    if (xeroContact) {
      const addresses = xeroContact.addresses.find(
        item => item.addressType === 'STREET',
      )

      xeroInitValues = {
        firstName: xeroContact.firstName,
        lastName: xeroContact.lastName,
        defaultCurrency: xeroContact.defaultCurrency,
        emailAddress: xeroContact.emailAddress,
        address1: addresses ? addresses.addressLine1 : '',
        address2: addresses ? addresses.addressLine2 : '',
        address3: addresses ? addresses.addressLine3 : '',
        address4: addresses ? addresses.addressLine4 : '',
        city: addresses ? addresses.city : '',
        country: addresses ? addresses.country : '',
        region: addresses ? addresses.region : '',
        postalCode: addresses ? addresses.postalCode : '',
        salesDefaultLineAmountType: 'EXCLUSIVE',
        salesDefaultAccountCode,
        attentionTo: addresses ? addresses.attentionTo : '',
      }
    }
  }
  const isXeroAvailable = apiXero.isHaveXeroAccess()

  const addUser = () => {
    setUsersHashMap(prev => {
      const ids = Object.keys(prev)
      const lastId = Math.max(...ids)
      if (ids.length) {
        return { ...prev, [lastId + 1]: { ...startedUser, id: lastId + 1 } }
      } else {
        return { [startedUser.id]: { ...startedUser } }
      }
    })
  }

  const onUserChange = ({ id, key, values }) => {
    setUsersHashMap(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [key]: {
          ...values,
        },
      },
    }))
  }

  const onUserDelete = id => {
    setUsersHashMap(prev => {
      const prevValue = { ...prev }
      delete prevValue[id]
      return prevValue
    })
  }

  const isUS = country && country.code === 'US'

  return (
    <Fragment>
      <Form
        onUpdate={(form, state) => {
          setName(state.name.value)
          if (!country && state && state.country && state.country.value) {
            setCountryId(state.country.value)
          }
        }}
        onSubmit={(form, isValid) => {
          handleSubmit(form, isValid, file)
        }}
      >
        <Typography className={classes.title} variant="h4">
          {mode === CLIENT_FORM_MODES.ADD ? 'Add Client' : 'Edit Client'}
        </Typography>
        <Card className={classes.card}>
          <Row>
            <Col xs={6}>
              <Input
                label="Name"
                name="name"
                placeholder="E.g Uber"
                validator={[required]}
                value={initValues.name}
                onBlur={onBlurInputName}
                cypress_id="clientName"
              />
              {country ? (
                <div className={classes.country}>
                  Country: {country ? country.name : 'N/A'}
                </div>
              ) : (
                <SelectDashboard
                  className={classes.input}
                  name="country"
                  label="Country"
                  options={countries.map(item => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  validator={[required]}
                  cypress_id="countryName"
                />
              )}
              {mode === CLIENT_FORM_MODES.EDIT ? (
                <Checkbox
                  label="Archived"
                  name="status"
                  initValue={initValues.status}
                  cypress_id="isArchived"
                />
              ) : null}

              <div className={classes.checkboxWrapper}>
                {mode === CLIENT_FORM_MODES.EDIT && (
                  <Fragment>
                    <Checkbox
                      label="Self-serve enabled"
                      name="isSelfServeEnabled"
                      initValue={defaultValues.isSelfServeEnabled}
                      input={{ disabled: !countryId }}
                      cypress_id="isSelfServeEnabled"
                    />
                    {defaultValues.isSelfServeEnabled && isUS && (
                      <Box marginTop={1}>
                        <Checkbox
                          label="EDDM Only"
                          name="eddmOnly"
                          initValue={isEddmSignup}
                          cypress_id="eddmOnly"
                        />
                      </Box>
                    )}
                  </Fragment>
                )}

                <SelectDashboard
                  className={classes.input}
                  name="industryGroup"
                  label="Industry Group"
                  options={industryGroupOptions}
                  value={industryGroup}
                  input={{
                    onChange: value => {
                      setIndustryGroup(value)
                      setIndustry(null)
                    },
                  }}
                  validator={[required]}
                  cypress_id="clientIndustryGroup"
                />
                <SelectDashboard
                  className={classes.input}
                  name="industry"
                  label="Industry"
                  options={industryOptions}
                  value={industry}
                  input={{
                    onChange: value => {
                      setIndustry(value)
                    },
                    disabled: !industryGroup,
                  }}
                  validator={[required]}
                  cypress_id="clientIndustry"
                />
              </div>

              {isAimFeatureEnabled && (
                <Row>
                  <Col xs={6}>
                    <Checkbox
                      label="Affiliate Partnership"
                      name="isAffiliate"
                      initValue={defaultValues.isAffiliate}
                      cypress_id="isAffiliate"
                    />
                  </Col>
                </Row>
              )}
              {mode === CLIENT_FORM_MODES.EDIT ? (
                <React.Fragment>
                  <div className={classes.apiCheckboxWrapper}>
                    <Checkbox
                      label="API Integration enabled"
                      name="apiAccessEnabled"
                      initValue={defaultValues.apiAccessEnabled}
                      input={{ disabled: !countryId }}
                      cypress_id="apiAccessEnabled"
                    />
                  </div>
                </React.Fragment>
              ) : null}
              {mode === CLIENT_FORM_MODES.EDIT ? (
                <div className={classes.sftpCheckboxWrapper}>
                  <Checkbox
                    label="Secure file transfer enabled"
                    name="sftpEnabled"
                    initValue={initValues.sftpEnabled}
                    input={{ disabled: !countryId }}
                    cypress_id="sftpEnabled"
                  />
                  <Tooltip
                    classes={{ tooltip: classes.whiteTooltip }}
                    title={
                      <span>
                        Enabling this feature allows users to send files to us
                        via <em>Settings → Secure File Transfer</em> on the
                        Promofy website
                      </span>
                    }
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      style={{
                        color: '#011533',
                        cursor: 'pointer',
                        marginLeft: '16px',
                      }}
                    />
                  </Tooltip>
                </div>
              ) : null}
              {isUS && (
                <Fragment>
                  <div className={classes.apiCheckboxWrapper}>
                    <Checkbox
                      label="Skip ADM Checkout enabled"
                      name="isSkipCheckoutEnabled"
                      initValue={defaultValues.isSkipCheckoutEnabled}
                      input={{ disabled: !countryId }}
                      cypress_id="isSkipCheckoutEnabled"
                    />
                  </div>
                  <div className={classes.apiCheckboxWrapper}>
                    <Checkbox
                      label="Address verification feature enabled"
                      name="addressVerificationEnabled"
                      initValue={initValues.addressVerificationEnabled}
                      input={{ disabled: !countryId }}
                      cypress_id="addressVerificationEnabled"
                    />
                  </div>
                  <Box marginTop={2} display="flex" alignItems="flex-end">
                    <div style={{ marginRight: '16px' }}>
                      <BudgetInput
                        label="Address verification limit - Braze workflow wave"
                        name="addressVerificationDailyBudget"
                        placeholder="50"
                        type="number"
                        initValue={initValues.addressVerificationDailyBudget}
                        cypress_id="addressVerificationDailyBudget"
                        suffix="per day"
                      />
                    </div>
                    <BudgetInput
                      name="addressVerificationMonthlyBudget"
                      placeholder="200"
                      type="number"
                      initValue={initValues.addressVerificationMonthlyBudget}
                      cypress_id="addressVerificationMonthlyBudget"
                      suffix="per month"
                      input={{}}
                    />
                    <div
                      style={{
                        height: '52px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          const dailyInput = document.querySelector(
                            '[name="addressVerificationDailyBudget"]',
                          )
                          const monthlyInput = document.querySelector(
                            '[name="addressVerificationMonthlyBudget"]',
                          )

                          if (dailyInput) {
                            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                              window.HTMLInputElement.prototype,
                              'value',
                            ).set
                            nativeInputValueSetter.call(dailyInput, '50')
                            dailyInput.dispatchEvent(
                              new Event('input', { bubbles: true }),
                            )
                          }

                          if (monthlyInput) {
                            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                              window.HTMLInputElement.prototype,
                              'value',
                            ).set
                            nativeInputValueSetter.call(monthlyInput, '200')
                            monthlyInput.dispatchEvent(
                              new Event('input', { bubbles: true }),
                            )
                          }
                        }}
                        style={{
                          fontSize: '14px',
                          color: 'rgba(0, 0, 0, 0.87)',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          letterSpacing: '0.16px',
                          lineHeight: '1.5',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                          paddingLeft: '16px',
                        }}
                      >
                        Restore to default
                      </button>
                    </div>
                  </Box>
                </Fragment>
              )}
            </Col>
            <Col xs={6} className={classes.dropzone}>
              <Dropzone
                name="logo"
                input={{
                  value: initValues.logo,
                  onChange: files => setFile(files[0]),
                }}
                meta={{}}
                cypress_id="logoDropzone"
              />
            </Col>
          </Row>
        </Card>
        {isUS && CONFIG.US_TAXES_ENABLED && mode === CLIENT_FORM_MODES.EDIT ? (
          <TaxExemptionSection
            handleOpenModal={handleAddStateOpenModal}
            isTaxExemptionEnabled={isTaxExemptionEnabled}
            setIsTaxExemptionEnabled={setIsTaxExemptionEnabled}
            handleDeleteTaxExemptionByState={handleDeleteTaxExemptionByState}
            uploadedDocumentsForUnitedStatesStates={
              uploadedDocumentsForUnitedStatesStates
            }
          />
        ) : null}
        <XeroClient
          isXeroAvailable={isXeroAvailable}
          xeroInfo={xeroInfo}
          tenant={tenant}
          xeroInitValues={xeroInitValues}
          currencies={currencies}
          usersHashMap={usersHashMap}
          addUser={addUser}
          onUserDelete={onUserDelete}
          onUserChange={onUserChange}
          isEditMode={true}
        />
        <div className={classes.buttonsWrapper}>
          <Button variant="outlined" value="Cancel" onClick={handleCancel} />
          <SubmitButton
            disabled={buttonsDisabled}
            variant="contained"
            value="Submit"
            cypress_id="submitBtn"
          />
        </div>
      </Form>
    </Fragment>
  )
}

export default withStyles(styles)(ClientForm)
